import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import { useQuery } from '@tanstack/react-query';
import { type ReactNode, useMemo } from 'react';
import type { DateWindow } from '../../lib/filters.hooks';
import { useClients } from '../../lib/filters.hooks';
import { useTRPC } from '../../lib/trpc';
import type { Stage } from '../filters/StageToggle';
import type { ScopeState } from '../filters/types';
import type { MediaFilters } from '../media/data/types';
import { toScopeFilters } from '../media/data/utils';
import { MediaCard } from '../media/shared/MediaCard';
import { AskAlisonBar } from '../workflows/AskAlisonBar';
import { ActivitySection } from './ActivitySection';
import { CreativesToWatch } from './CreativesToWatch';
import { DashboardGreeting } from './DashboardGreeting';
import { HeroMetrics } from './HeroMetrics';
import { HERO_GRID_SX, SMALL_CARD_HEIGHT, TALL_CARD_HEIGHT } from './layout';
import { ReportsAndOutcomes } from './ReportsAndOutcomes';
import { StartWorkflowSection } from './StartWorkflowSection';
import { TooltipTrackingProvider } from './TooltipTracking';

// Figma 12054:64113: one centred 1248px column, 32px between sections.
export const DASHBOARD_COLUMN_MAX = 1248;

interface DashboardBoardProps {
  // HOME's Signals section, owned by the route.
  signals?: ReactNode;
  scope: ScopeState;
  filters: MediaFilters;
  accountIds: number[];
  dateWindow: DateWindow;
  stage: Stage;
}

function SmallCardSkeleton(): ReactNode {
  return (
    <MediaCard sx={{ p: 3, height: SMALL_CARD_HEIGHT }}>
      <Skeleton variant="text" width={100} />
      <Skeleton variant="text" width={80} height={32} />
    </MediaCard>
  );
}

function HeroMetricsSkeleton(): ReactNode {
  return (
    <Box sx={HERO_GRID_SX}>
      <MediaCard sx={{ p: 3, height: TALL_CARD_HEIGHT }}>
        <Skeleton variant="text" width={160} />
        <Skeleton variant="circular" width={132} height={132} sx={{ mx: 'auto', mt: 3 }} />
      </MediaCard>
      <MediaCard sx={{ p: 3, height: TALL_CARD_HEIGHT }}>
        <Skeleton variant="text" width={160} />
        <Skeleton variant="rounded" height={200} sx={{ mt: 3 }} />
      </MediaCard>
      {['left', 'right'].map((column) => (
        <Box key={column} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <SmallCardSkeleton />
          <SmallCardSkeleton />
        </Box>
      ))}
    </Box>
  );
}

export function DashboardBoard({
  scope,
  filters,
  accountIds,
  dateWindow,
  stage,
  signals,
}: DashboardBoardProps): ReactNode {
  const trpc = useTRPC();
  const { data: clients } = useClients();
  const enabled = (clients?.length ?? 0) > 0;

  const boardScope = useMemo(() => {
    const scopeFilters = toScopeFilters(
      filters.countries,
      filters.creativeTypes,
      filters.objectives,
    );
    return {
      dateRange: dateWindow,
      kpi: scope.leadingKpi,
      ...(scopeFilters ? { filters: scopeFilters } : {}),
      ...(accountIds.length ? { accountIds } : {}),
    };
  }, [
    dateWindow,
    scope.leadingKpi,
    filters.countries,
    filters.creativeTypes,
    filters.objectives,
    accountIds,
  ]);

  const heroMetrics = useQuery({
    ...trpc.dashboard.heroMetrics.queryOptions(boardScope),
    enabled,
  });

  return (
    <Box
      data-testid="dashboard-column"
      sx={{
        width: '100%',
        maxWidth: DASHBOARD_COLUMN_MAX,
        minWidth: 0,
        mx: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: '32px',
      }}
    >
      <DashboardGreeting />
      {heroMetrics.isError ? (
        <Alert severity="error">
          We couldn’t load the dashboard for this scope. Adjust the filters and try again.
        </Alert>
      ) : heroMetrics.data ? (
        <HeroMetrics data={heroMetrics.data} />
      ) : (
        <HeroMetricsSkeleton />
      )}
      <AskAlisonBar scope={scope} />
      <TooltipTrackingProvider>
        <CreativesToWatch scope={boardScope} enabled={enabled} />
      </TooltipTrackingProvider>
      {signals}
      <ReportsAndOutcomes />
      <StartWorkflowSection stage={stage} accountIds={accountIds} />
      <ActivitySection accountIds={accountIds} />
    </Box>
  );
}
